// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { OAuthScope } from '@/account/api/get-connected-applications';
import { OAUTH_SCOPE_DISPLAY } from '@/account/constants/connected-applications';
import { CachedOAuthScopeDeltaListProperty } from '@/audit/api/types';
import { TooltipTypography } from '@/components/tooltip-typography';
import { Stack, Typography } from '@mui/material';

interface Props {
  property: CachedOAuthScopeDeltaListProperty;
}

/** Shows the permissions a change to a connected application added and removed. */
export function AuditOAuthScopeDeltaListLabel(props: Props) {
  const added = props.property.newValue?.added ?? [];
  const removed = props.property.newValue?.removed ?? [];

  if (added.length === 0 && removed.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>
      {added.length > 0 && <OAuthScopeSection title="Added" scopes={added} />}
      {removed.length > 0 && (
        <OAuthScopeSection title="Removed" scopes={removed} />
      )}
    </Stack>
  );
}

interface SectionProps {
  title: string;
  scopes: OAuthScope[];
}

/** Lists one side of the change, each permission explained on hover. */
function OAuthScopeSection(props: SectionProps) {
  return (
    <Stack sx={{ marginLeft: 2 }}>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.title}:
      </Typography>
      {props.scopes.map((scope) => (
        <Stack key={scope} direction="row" sx={{ marginLeft: 2 }}>
          <TooltipTypography
            variant="caption"
            tooltip={OAUTH_SCOPE_DISPLAY[scope].description}
          >
            {OAUTH_SCOPE_DISPLAY[scope].label}
          </TooltipTypography>
        </Stack>
      ))}
    </Stack>
  );
}
